/**
 * @fileoverview Account Setup Page - 2-Step Wizard for Guest Purchases
 * @module AccountSetupPage
 * 
 * Business Context:
 * - Handles account completion after guest purchases
 * - Collects password + property information for personalization
 * - Immediately authenticates user and redirects to personalized dashboard
 * 
 * Technical Context:
 * - Validates setup tokens from email links
 * - Integrates with existing Property Service for address validation
 * - Uses Auth Service patterns for account completion
 * 
 * Related Documents:
 * - PRD: Section 4.2 (Onboarding Phase) and 5.2 (Personalization System)
 * - Architecture: Section 3.1 (Authentication) and 3.4 (Property Module)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { CountySelector } from '../components/property/CountySelector';
import { 
  Eye, 
  EyeOff, 
  Home, 
  Building2, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle,
  MapPin,
  User,
  Lock,
  DollarSign
} from 'lucide-react';

interface AccountSetupData {
  // Step 1: Account Creation
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  
  // Step 2: Property Information  
  addressLine1: string;
  addressLine2?: string;
  city: string;
  zipCode: string;
  countyId: number | null;
  propertyType: 'single_family' | 'condo' | 'townhouse' | 'mobile_home';
  homesteadStatus: boolean;
  yearBuilt?: number;
  squareFootage?: number;
  
  // Appeal enhancement fields
  assessedValue?: number;           // Current county assessed value in dollars (converted to cents on submit)
}

interface ValidationErrors {
  [key: string]: string;
}

const AccountSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUserAndTokens } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState<AccountSetupData>({
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zipCode: '',
    countyId: null,
    propertyType: 'single_family',
    homesteadStatus: true
    // assessedValue is optional - omit rather than set undefined
  });

  // Validation
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [addressValidation, setAddressValidation] = useState<{
    isValid: boolean;
    county?: { id: number; name: string };
    validationMessages?: string[];
  } | null>(null);

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setIsLoading(false);
      return;
    }

    validateSetupToken();
  }, [token, navigate]);

  const validateSetupToken = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.auth.validateSetupToken(token);
      
      if (response.data.success) {
        setTokenValid(true);
        setUserEmail(response.data.data.email);
        setFormData(prev => ({
          ...prev,
          firstName: response.data.data.firstName || '',
          lastName: response.data.data.lastName || ''
        }));
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = async (): Promise<boolean> => {
    const newErrors: ValidationErrors = {};

    // Address validation
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^(7[3-9]\d{3}|885\d{2})(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid Texas ZIP code';
    }

    // County validation
    if (!formData.countyId) {
      newErrors.county = 'Please select your county';
    }

    // If basic validation passes, validate address with county (public endpoint for account setup)
    if (Object.keys(newErrors).length === 0 && formData.countyId) {
      try {
        const response = await apiClient.properties.validateAddressPublic({
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          zipCode: formData.zipCode,
          countyId: formData.countyId,
          state: 'TX'
        });

        if (response.data.success) {
          setAddressValidation(response.data.data);
          if (!response.data.data.isValid) {
            newErrors.address = response.data.data.validationMessages?.[0] || 'Address validation failed';
          }
        } else {
          newErrors.address = response.data.error?.message || 'Unable to validate address. Please check your input.';
        }
      } catch (error) {
        console.error('Address validation error:', error);
        newErrors.address = 'Address validation temporarily unavailable';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Next = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleFinalSubmit = async () => {
    if (!(await validateStep2())) {
      return;
    }

    try {
      setIsLoading(true);

      const response = await apiClient.auth.completeAccountSetup({
        token,
        accountData: {
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim()
        },
        propertyData: {
          addressLine1: formData.addressLine1.trim(),
          addressLine2: formData.addressLine2?.trim() || null,
          city: formData.city.trim(),
          zipCode: formData.zipCode.trim(),
          countyId: formData.countyId,
          propertyType: formData.propertyType,
          homesteadStatus: formData.homesteadStatus,
          yearBuilt: formData.yearBuilt || null,
          squareFootage: formData.squareFootage || null,
          // Convert dollars to cents for storage
          assessedValue: formData.assessedValue ? Math.round(formData.assessedValue * 100) : null
        }
      });

      if (response.data.success) {
        // Set user and tokens, and fetch full profile including properties
        await setUserAndTokens(response.data.data.user, response.data.data.tokens.accessToken);
        
        // Redirect to personalized dashboard
        navigate('/dashboard');
      } else {
        setErrors({ submit: response.data.error?.message || 'Setup failed. Please try again.' });
      }
    } catch (error) {
      console.error('Account setup error:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Partial address validation for better UX (without county requirement)
  const validateAddressPartial = async () => {
    // Only validate if we have basic address components
    if (!formData.addressLine1.trim() || !formData.city.trim() || !formData.zipCode.trim()) {
      return;
    }

    // Skip if ZIP code doesn't look like Texas
    if (!/^(7[3-9]\d{3}|885\d{2})(-\d{4})?$/.test(formData.zipCode)) {
      return;
    }

    try {
      const response = await apiClient.properties.validateAddressPartialPublic({
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        zipCode: formData.zipCode,
        state: 'TX'
      });

      if (response.data.success) {
        // Store partial validation results
        setAddressValidation(prev => ({
          ...prev,
          ...response.data.data,
          isValid: false // Still need county selection for full validation
        }));
      }
    } catch (error) {
      // Silently fail for partial validation
      console.log('Partial validation error (non-critical):', error);
    }
  };

  const updateFormData = (field: keyof AccountSetupData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific errors when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Trigger partial address validation for address fields
    if (['addressLine1', 'city', 'zipCode'].includes(field)) {
      // Debounce the validation call
      setTimeout(() => validateAddressPartial(), 1000);
    }
  };

  // Loading state
  if (isLoading && tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">Validating setup link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    const isMissingToken = !token;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">
            {isMissingToken ? 'Setup Link Required' : 'Invalid Setup Link'}
          </h1>
          <p className="text-slate-300 mb-8">
            {isMissingToken 
              ? 'To set up your account, please use the setup link sent to your email after purchase. Check your email and click the "Complete Account Setup" button.'
              : 'This account setup link is invalid or has expired. Please check your email for a new link or contact support.'
            }
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/check-email')}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Check Email Instructions
            </button>
            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const propertyTypes = [
    { value: 'single_family', label: 'Single Family Home', icon: Home, description: 'Detached house on its own lot' },
    { value: 'condo', label: 'Condominium', icon: Building2, description: 'Unit in a multi-unit building' },
    { value: 'townhouse', label: 'Townhouse', icon: Home, description: 'Multi-story unit sharing walls' },
    { value: 'mobile_home', label: 'Mobile Home', icon: Home, description: 'Manufactured housing unit' }
  ] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Complete Your Account Setup</h1>
          <p className="text-slate-300">Welcome! Let's get your account ready and personalize your course.</p>
          <p className="text-sm text-slate-400 mt-2">Setting up account for: {userEmail}</p>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 1 ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-400'
            }`}>
              {currentStep > 1 ? <CheckCircle2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
            </div>
            <div className={`w-20 h-1 ${currentStep >= 2 ? 'bg-teal-600' : 'bg-slate-700'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 2 ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-400'
            }`}>
              <MapPin className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Lock className="w-12 h-12 text-teal-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Password</h2>
                <p className="text-gray-600">Secure your account and complete your profile</p>
              </div>

              {/* Password Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => updateFormData('password', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Create a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => updateFormData('firstName', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="First name"
                  />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => updateFormData('lastName', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Last name"
                  />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <button
                onClick={handleStep1Next}
                className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center justify-center space-x-2"
              >
                <span>Continue to Property Details</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <MapPin className="w-12 h-12 text-teal-600 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Property Information</h2>
                <p className="text-gray-600">Tell us about your property so we can personalize your course</p>
              </div>

              {/* Address Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                  <input
                    type="text"
                    value={formData.addressLine1}
                    onChange={(e) => updateFormData('addressLine1', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.addressLine1 ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="123 Main Street"
                  />
                  {errors.addressLine1 && <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apartment/Unit (Optional)</label>
                  <input
                    type="text"
                    value={formData.addressLine2}
                    onChange={(e) => updateFormData('addressLine2', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Apt 2B, Unit 5, etc."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => updateFormData('city', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Houston"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                    <input
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => updateFormData('zipCode', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.zipCode ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="77001"
                    />
                    {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                  </div>
                </div>

                {/* County Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">County</label>
                  <CountySelector
                    value={formData.countyId}
                    onChange={(countyId, county) => updateFormData('countyId', countyId)}
                    error={errors.county}
                    placeholder="Select your Texas county"
                    required={true}
                    searchable={true}
                  />
                </div>

                {errors.address && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-700 text-sm">{errors.address}</p>
                  </div>
                )}

                {addressValidation?.isValid && addressValidation.county && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-700 text-sm">
                      ✓ Address validated - {addressValidation.county.name} County
                    </p>
                  </div>
                )}
              </div>

              {/* Property Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Property Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {propertyTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => updateFormData('propertyType', type.value)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          formData.propertyType === type.value
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          <Icon className={`w-5 h-5 ${
                            formData.propertyType === type.value ? 'text-teal-600' : 'text-gray-400'
                          }`} />
                          <span className={`font-medium ${
                            formData.propertyType === type.value ? 'text-teal-900' : 'text-gray-900'
                          }`}>
                            {type.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Homestead Status */}
              <div>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.homesteadStatus}
                    onChange={(e) => updateFormData('homesteadStatus', e.target.checked)}
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">This is my primary residence (Homestead)</span>
                    <p className="text-sm text-gray-500">Check if you qualify for homestead exemption</p>
                  </div>
                </label>
              </div>

              {/* Current Assessed Value - CRITICAL FOR APPEAL */}
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3 mb-3">
                  <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <label className="block text-sm font-bold text-amber-900">Current Assessed Value</label>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Find this on your county appraisal notice. This is critical for calculating your potential savings.
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-500 font-medium">$</span>
                  <input
                    type="text"
                    value={formData.assessedValue ? formData.assessedValue.toLocaleString() : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      updateFormData('assessedValue', value ? parseInt(value) : undefined);
                    }}
                    className="w-full pl-8 pr-4 py-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                    placeholder="350,000"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">
                  You can also add this later from your dashboard if you don't have it handy.
                </p>
              </div>

              {/* Optional Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year Built (Optional)</label>
                  <input
                    type="number"
                    value={formData.yearBuilt || ''}
                    onChange={(e) => updateFormData('yearBuilt', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="2020"
                    min="1800"
                    max={new Date().getFullYear()}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Square Footage (Optional)</label>
                  <input
                    type="number"
                    value={formData.squareFootage || ''}
                    onChange={(e) => updateFormData('squareFootage', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="2500"
                    min="100"
                  />
                </div>
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{errors.submit}</p>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleFinalSubmit}
                  disabled={isLoading}
                  className="flex-1 bg-teal-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Setting Up...</span>
                    </>
                  ) : (
                    <>
                      <span>Complete Setup</span>
                      <CheckCircle2 className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSetupPage;